import React from 'react';
import ReactHtmlParser from 'react-html-parser';

import DataTable from '@components/DataTable';
import LazyImgixImage from '@components/LazyImgixImage';
import ProjectsPaywall from '@components/ProjectsPaywall';
import useCurrentUser from '@hooks/useCurrentUser';
import { DatabaseProps, ProjectColumnConfigFn } from '@interfaces/projects';
import { getImgixSrc } from '@utils/images';

import CSVDownloadLink from '../CSVDownloadLink';
import Map from './Map';
import TopSection from './TopSection';

const PROJECT_IMAGE_PATH = 'projects/ai-data-center-database/logos';

const columnConfigFn: ProjectColumnConfigFn = (column) => {
  return {
    ...column,
    cell: (info) => {
      if (info.column.getIsFirstColumn()) {
        const cellVal = info.getValue() as string;
        const imageFileName = info.row.getValue('logo') as string;
        return (
          <div className="company flex items-center justify-start gap-12">
            <LazyImgixImage
              alt={cellVal}
              className="h-32 rounded-full align-middle"
              src={getImgixSrc({
                path: `${PROJECT_IMAGE_PATH}/${imageFileName}`,
                isEnvDependent: false,
              })}
            />
            <div>{cellVal}</div>
          </div>
        );
      }
      let cellValue = info.getValue() as string;
      if (cellValue?.includes('<a href=')) {
        cellValue = cellValue.replace(
          '<a href=',
          '<a class="hover:text-primary-magenta dark:hover:text-primary-pink underline" href='
        );
      }
      return ReactHtmlParser(cellValue);
    },
  };
};

const Layout = <TData, TValue>({
  data,
  paywallData,
  project,
  unlocked,
}: DatabaseProps<TData, TValue>) => {
  const { currentUser } = useCurrentUser();
  const isCurrentUserCorpOrGroupSubscriber = currentUser?.is_corp_or_group_subscriber;

  return (
    <>
      <TopSection
        accessLevel={project.accessLevel}
        authors={project.authors}
        description={project.description}
        title={project.title}
        titleCallout={project.titleCallout}
      />
      {!unlocked ? (
        <>
          <div className="relative before:absolute before:bottom-0 before:left-0 before:z-10 before:h-[300px] before:w-full before:bg-[linear-gradient(to_top,#fff_0%,rgba(255,255,255,0)_100%)] before:content-[''] dark:before:bg-[linear-gradient(to_top,#1a1a1a_0%,rgba(26,26,26,0)_100%)]"></div>
          <div className="sticky bottom-0 z-50">
            <ProjectsPaywall {...paywallData} leadType={project.leadType} />
          </div>
        </>
      ) : (
        <div className="w-container-lg mx-auto mt-32 max-w-container-lg px-16 sm:mt-48">
          <span id="no-paywall"></span>
          <Map rows={data.data} />
          <DataTable
            classNameTd="group-[.active]:bg-pro-gold group-[.fade-out]:bg-white group-[.fade-out]:duration-500"
            columns={data.columns}
            columnConfigFn={columnConfigFn}
            data={data.data}
            hiddenCols={data.hiddenCols}
            pinnedCols={data.pinnedCols}
            projectId={project.id}
            sortCols={data.sortCols}
            showSearch
          />
          {isCurrentUserCorpOrGroupSubscriber && (
            <CSVDownloadLink id={project.id} slug={project.slug} />
          )}
        </div>
      )}
    </>
  );
};

export default Layout;
